import { Banana, Box, BrainCircuit, Cloud, Hexagon, Orbit, Shell } from "lucide-react";
import { DATASTART_IMAGE_MODELS, DATASTART_TEXT_UNAVAILABLE_MESSAGE } from './datastartImageModels';
import { ASTRAFLOW_IMAGE_MODELS } from './astraflowImageModels';
import { ASTRAFLOW_ASTRA_MODELS, normalizeAstraProviderId } from './astraflowAstraModels';
import {
  DREAMFLY_IMAGE_MODELS,
  DREAMFLY_LLM_PROVIDER_ID,
  normalizeDreamflyImageModelId,
} from "./dreamflyModels";

// 模型列表：相同模型复用同一 icon，相同线路复用同一颜色。
// recommendation 只描述适用场景；真实供应商由 id 在内部映射，不向用户暴露。
// color 为供应商线路品牌标识色（跨主题保持一致），保留
export const GLOBAL_AI_PROVIDERS = [
  { id: "wangsugem", name: "Gemini_线路1", icon: Banana, color: "#22c55e", recommendation: "推荐长文本与复杂分镜" },
  { id: "wangsugpt", name: "ChatGPT_线路1", icon: BrainCircuit, color: "#22c55e", recommendation: "推荐高质量文案与画面" },
  { id: "huobao", name: "Gemini_线路2", icon: Banana, color: "#fb923c", recommendation: "推荐日常快速生成" },
  { id: "huobao_gpt", name: "ChatGPT_线路2", icon: BrainCircuit, color: "#fb923c", recommendation: "推荐常规图文创作" },
  { id: "huobao_gpt_image_cheap", name: "ChatGPT_线路2-4K图", icon: BrainCircuit, color: "#fb923c", recommendation: "推荐批量 4K 生图" },
  { id: "astraflow_gemini", name: "Gemini_线路3", icon: Banana, color: "#a78bfa", recommendation: "推荐长上下文与视觉理解" },
  { id: "astraflow_openai", name: "ChatGPT_线路3", icon: BrainCircuit, color: "#a78bfa", recommendation: "推荐综合创作与精细生图" },
  { id: "astraflow_openai_luna", name: "GPT-5.6 Luna_线路3", icon: BrainCircuit, color: "#a78bfa", recommendation: "推荐轻量任务与低成本生成" },
  { id: "astraflow_openai_terra", name: "GPT-5.6 Terra_线路3", icon: BrainCircuit, color: "#a78bfa", recommendation: "推荐均衡创作与复杂任务" },
  { id: "astraflow_openai_sol", name: "GPT-5.6 Sol_线路3", icon: BrainCircuit, color: "#a78bfa", recommendation: "推荐高难度推理与精细创作" },
  ...ASTRAFLOW_ASTRA_MODELS.map(({ id, label, recommendation }) => ({
    id, name: label, recommendation, icon: BrainCircuit, color: '#a78bfa',
  })),
  ...ASTRAFLOW_IMAGE_MODELS.map(({ id, label }) => ({
    id, name: label, icon: BrainCircuit, color: '#a78bfa',
    recommendation: '纯生图 · 文字生图与多图参考', capabilities: ['image'],
  })),
  { id: "astraflow_grok", name: "Grok_线路3", icon: Orbit, color: "#a78bfa", recommendation: "推荐创意构图与视觉探索" },
  { id: "omini", name: "Gemini_线路4", icon: Banana, color: "#facc15", recommendation: "推荐多模态备用线路" },
  { id: "omini_openai", name: "ChatGPT_线路4", icon: BrainCircuit, color: "#facc15", recommendation: "推荐图文生成备用线路" },
  { id: "doubao", name: "豆包", icon: Box, color: "#34d399", recommendation: "推荐中文创作与快速生图" },
  { id: "wanx", name: "万相", icon: Hexagon, color: "#818cf8", recommendation: "推荐商品图与局部修图" },
  ...DATASTART_IMAGE_MODELS.map(({ id, label }) => ({
    id, name: label, icon: BrainCircuit, color: '#38bdf8',
    recommendation: '纯生图 · 1024/1536px · 单图参考与蒙版编辑', capabilities: ['image'],
  })),
  ...DREAMFLY_IMAGE_MODELS.map(({ id, label, model }) => ({
    id,
    name: label,
    icon: Cloud,
    color: "#06b6d4",
    recommendation: model,
    capabilities: ["image"],
  })),
  {
    id: DREAMFLY_LLM_PROVIDER_ID,
    name: "Dreamfly LLM",
    icon: Cloud,
    color: "#06b6d4",
    recommendation: "动态加载 Dreamfly 文字模型目录",
    capabilities: ["text"],
  },
  // { id: "deepseek", name: "DeepSeek & Vidu", icon: BrainCircuit, color: "#60a5fa", desc: "DeepSeek推理分镜，Vidu生成图片" },
  { id: "claude_opus_mj", name: "Claude Opus + MJ", icon: Shell, color: "#a78bfa", recommendation: "推荐高审美概念与艺术图" },
  // { id: "moyuto_gpt", name: "ChatGPT_Moyuto", icon: BrainCircuit, color: "#84cc16", desc: "GPT-4o-mini / GPT Image 1" },
  // { id: "mock_agent", name: "演示模式", icon: BrainCircuit, color: "#34d399", desc: "使用示例数据快速体验流程" },
];

export const providerSupportsCapability = (provider, capability) => (
  !Array.isArray(provider?.capabilities) || provider.capabilities.includes(capability)
);

export const TEXT_AI_PROVIDERS = GLOBAL_AI_PROVIDERS.filter((provider) => (
  providerSupportsCapability(provider, "text")
));

// 顶部总模型菜单允许直接切换 DataStart 纯图片线路；文字专用下拉仍只使用
// TEXT_AI_PROVIDERS，避免在需要文字能力的表单里误导用户。
const DATASTART_MENU_PROVIDER_IDS = new Set(DATASTART_IMAGE_MODELS.map(({ id }) => id));
export const MODEL_MENU_PROVIDERS = GLOBAL_AI_PROVIDERS.filter((provider) => (
  providerSupportsCapability(provider, "text") || DATASTART_MENU_PROVIDER_IDS.has(provider.id)
));

export const IMAGE_AI_PROVIDERS = GLOBAL_AI_PROVIDERS.filter((provider) => (
  providerSupportsCapability(provider, "image")
));

export const normalizeGlobalProvider = (provider) => {
  const id = normalizeAstraProviderId(provider);
  return GLOBAL_AI_PROVIDERS.some((item) => item.id === id) ? id : "wangsugem";
};

export const normalizeTextProvider = (provider) => {
  const id = normalizeAstraProviderId(provider);
  return TEXT_AI_PROVIDERS.some((item) => item.id === id) ? id : "wangsugem";
};

export const normalizeModelMenuProvider = (provider) => {
  const id = normalizeAstraProviderId(provider);
  return MODEL_MENU_PROVIDERS.some((item) => item.id === id) ? id : "wangsugem";
};

// DataStart 只生图。混合工作流可以使用用户另行选择的文字线路，但不能临时补默认线路。
export const resolveDatastartWorkflowTextProvider = (provider) => {
  const id = normalizeAstraProviderId(provider);
  if (!TEXT_AI_PROVIDERS.some(item => item.id === id)) {
    throw new Error(`${DATASTART_TEXT_UNAVAILABLE_MESSAGE}；请另行选择文字模型`);
  }
  return id;
};

export const normalizeImageProvider = (provider) => {
  const id = normalizeAstraProviderId(provider);
  const dreamflyModelId = normalizeDreamflyImageModelId(id);
  if (dreamflyModelId) return dreamflyModelId;
  return IMAGE_AI_PROVIDERS.some((item) => item.id === id) ? id : "wangsugem";
};

export const getGlobalProviderRecommendation = (provider) => (
  GLOBAL_AI_PROVIDERS.find((item) => item.id === normalizeAstraProviderId(provider))?.recommendation || ""
);

export const toGlobalProviderOption = (provider) => ({
  value: provider.id,
  label: provider.recommendation ? `${provider.name} · ${provider.recommendation}` : provider.name,
});
